import type { Artist } from "../types";
import {
  addressFieldsFromEntity,
  formatDisplayAddress,
  formatPostalAddress,
  primaryCityFromEntity,
  type AddressFormState,
} from "./addressForm";

export const ARTIST_GENRES: readonly string[] = [
  "DJ / Electronic",
  "Live band",
  "Singer / vocalist",
  "Rap / hip-hop",
  "Bollywood / film",
  "Classical / instrumental",
  "Comedy / host",
  "Folk / regional",
  "Other",
];

export type ArtistFormState = AddressFormState & {
  name: string;
  stage_name: string;
  email: string;
  phone: string;
  genre: string;
  manager_name: string;
  manager_phone: string;
  notes: string;
  base_cost: string;
};

export const emptyArtistForm: ArtistFormState = {
  name: "",
  stage_name: "",
  email: "",
  phone: "",
  genre: "",
  manager_name: "",
  manager_phone: "",
  notes: "",
  base_cost: "",
  address_line1: "",
  address_line2: "",
  landmark: "",
  city: "",
  district: "",
  state: "",
  pincode: "",
  country: "India",
};

/**
 * Reads the pre-split ``contact`` string on older artist records.
 *
 * Only the read direction is still needed: new records write ``email`` and
 * ``phone`` separately, and ``formatArtistContactSummary`` renders them.
 */
function splitLegacyContact(contact?: string | null): { email: string; phone: string } {
  const raw = contact?.trim() ?? "";
  if (!raw) return { email: "", phone: "" };
  if (raw.includes("@") && !raw.includes("·")) {
    return { email: raw, phone: "" };
  }
  const parts = raw.split("·").map((p) => p.trim());
  if (parts.length >= 2) {
    const emailPart = parts.find((p) => p.includes("@")) ?? "";
    const phonePart = parts.find((p) => !p.includes("@")) ?? parts[0];
    return { email: emailPart, phone: phonePart };
  }
  if (/^[\d\s+\-()]+$/.test(raw)) return { email: "", phone: raw };
  return { email: raw, phone: "" };
}

export function artistToForm(artist: Artist): ArtistFormState {
  const { email, phone } =
    artist.email || artist.phone
      ? { email: artist.email ?? "", phone: artist.phone ?? "" }
      : splitLegacyContact(artist.contact);
  return {
    name: artist.name,
    stage_name: artist.stage_name ?? "",
    email,
    phone,
    genre: artist.genre ?? "",
    manager_name: artist.manager_name ?? "",
    manager_phone: artist.manager_phone ?? "",
    notes: artist.notes ?? "",
    base_cost: artist.base_cost != null ? String(artist.base_cost) : "",
    ...addressFieldsFromEntity(artist),
  };
}

export function buildArtistPayload(form: ArtistFormState) {
  const address: AddressFormState = {
    address_line1: form.address_line1,
    address_line2: form.address_line2,
    landmark: form.landmark,
    city: form.city,
    district: form.district,
    state: form.state,
    pincode: form.pincode,
    country: form.country,
  };

  return {
    name: form.name.trim(),
    stage_name: form.stage_name.trim() || null,
    email: form.email.trim() || null,
    phone: form.phone.trim() || null,
    genre: form.genre.trim() || null,
    manager_name: form.manager_name.trim() || null,
    manager_phone: form.manager_phone.trim() || null,
    notes: form.notes.trim() || null,
    current_location: formatPostalAddress(address) || null,
    address_line1: form.address_line1.trim() || null,
    address_line2: form.address_line2.trim() || null,
    landmark: form.landmark.trim() || null,
    city: form.city.trim() || null,
    district: form.district.trim() || null,
    state: form.state.trim() || null,
    pincode: form.pincode.trim() || null,
    country: form.country.trim() || "India",
    base_cost: Number(form.base_cost) || 0,
  };
}

/** Public-facing name: stage name when set, otherwise the legal/billing name. */
export function artistDisplayName(artist: Pick<Artist, "name" | "stage_name">): string {
  return artist.stage_name?.trim() || artist.name;
}

export function formatArtistDisplayLocation(artist: Artist): string {
  return formatDisplayAddress(artist);
}

export function formatArtistContactSummary(artist: Artist): string {
  const email = artist.email?.trim();
  const phone = artist.phone?.trim();
  if (email || phone) {
    return [email, phone].filter(Boolean).join(" · ");
  }
  return artist.contact?.trim() || "—";
}

export function artistPrimaryCity(artist: Artist): string {
  return primaryCityFromEntity(artist);
}
